import { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import type { Location } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, Lock } from 'lucide-react';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import { useAuthStore } from '../../store/authStore';
import { parseLoginError } from './parseLoginError';
import { BRAND } from '../../config/brand';

export default function Login() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, resendVerification } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  const loginSchema = useMemo(() => z.object({
    email: z.string().email(t('login.validation.invalidEmail')),
    password: z.string().min(1, t('login.validation.passwordRequired')),
  }), [t]);

  type LoginForm = z.infer<typeof loginSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    setUnverifiedEmail(null);
    try {
      await login(data.email, data.password);
      toast.success(t('login.toast.success'));
      const raw = (location.state as { from?: Location })?.from?.pathname;
      const from = !raw || raw === '/login' ? '/dashboard' : raw;
      navigate(from, { replace: true });
    } catch (error) {
      const axiosError = error as { response?: { data?: { error?: { code?: string } } } };
      if (axiosError.response?.data?.error?.code === 'AUTH_006') {
        setUnverifiedEmail(data.email);
      } else {
        toast.error(t(parseLoginError(error)));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!unverifiedEmail) return;
    setResending(true);
    try {
      await resendVerification(unverifiedEmail);
      toast.success(t('login.toast.verificationResent'));
    } catch {
      toast.error(t('login.toast.failed'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <Card variant="elevated" padding="lg" className="backdrop-blur-xl bg-bg-secondary/80">
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <img src="/logo.svg" alt={BRAND.name} className="h-10 w-auto" />
        </div>

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-text-primary">{t('login.title')}</h1>
          <p className="text-text-secondary mt-1">
            {t('login.subtitle')}
          </p>
        </div>

        {/* Email-not-verified banner */}
        {unverifiedEmail && (
          <div className="mb-4 p-3 rounded-lg bg-warning/10 border border-warning/30 text-sm text-text-primary">
            <p className="mb-2">{t('login.unverified.message')}</p>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="text-accent-primary hover:text-accent-primary-hover font-medium disabled:opacity-50"
            >
              {resending ? t('login.unverified.resending') : t('login.unverified.resendCta')}
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label={t('login.email')}
            type="email"
            autoComplete="email"
            placeholder={t('login.emailPlaceholder')}
            leftIcon={Mail}
            error={errors.email?.message}
            {...register('email')}
          />

          <Input
            label={t('login.password')}
            type="password"
            autoComplete="current-password"
            placeholder={t('login.passwordPlaceholder')}
            leftIcon={Lock}
            error={errors.password?.message}
            {...register('password')}
          />

          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 text-text-secondary">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-border bg-bg-tertiary"
              />
              {t('login.remember')}
            </label>
            <Link
              to="/forgot-password"
              className="text-accent-primary hover:text-accent-primary-hover"
            >
              {t('login.forgot')}
            </Link>
          </div>

          <p className="text-center text-sm">
            <Link to="/magic-link" className="text-text-secondary hover:text-accent-primary">
              {t('login.magicLinkCta')}
            </Link>
          </p>

          <Button type="submit" fullWidth loading={loading}>
            {t('login.submit')}
          </Button>
        </form>

        {/* Footer */}
        <p className="text-center text-sm text-text-secondary mt-6">
          {t('login.noAccount')}{' '}
          <Link
            to="/register"
            className="text-accent-primary hover:text-accent-primary-hover font-medium"
          >
            {t('login.signUp')}
          </Link>
        </p>
      </Card>
    </div>
  );
}
